<template>
    <Transition name="action-bar-fade">
        <div v-if="hasSelection" class="action-bar-wrapper" :class="{ 'is-measured': measured }" ref="actionBarWrapperRef">
            <div class="action-bar">
                <button class="action-bar-deselect-btn" @click="ns.clearSelection()">
                    {{ selectionLabel }}
                    <Icon name="close" size="xs" color="inherit" />
                </button>
                <div class="action-bar-actions">
                    <button
                        v-for="item in visibleItems"
                        :key="item.name"
                        :disabled="isDisabled(item)"
                        :data-tooltip="actionBarIcons[item.type]?.iconOnly ? item.label : null"
                        :aria-label="actionBarIcons[item.type]?.iconOnly ? item.label : null"
                        class="action-bar-btn"
                        @click="handleAction(item)"
                    >
                        <Icon v-if="actionBarIcons[item.type]?.icon" :name="actionBarIcons[item.type].icon" size="xs" color="inherit" />
                        <span v-if="!actionBarIcons[item.type]?.iconOnly">{{ item.label }}</span>
                    </button>
                    <EjsDropdownbutton
                        v-if="hasOverflow"
                        ref="moreButtonRef"
                        :items="overflowItems"
                        cssClass="action-bar-more-btn e-flat"
                        @select="handleOverflowSelect"
                    ><span class="action-bar-more-content"><Icon :name="actionBarIcons.ellipsis?.icon" size="xs" color="inherit" />More</span></EjsDropdownbutton>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script setup>
import {
    ref, computed, watch, onMounted, onUnmounted, nextTick
} from 'vue';
import { navigationStore } from '@common/navigation/navigationStore';
import { actionBarIcons } from '@common/icon/iconRegistry';
import Icon from '@common/icon/Icon.vue';
import EjsDropdownbutton from '@common/syncfusion/dropdownButton';

const ns = navigationStore();

const measured = ref(false);
const visibleCount = ref(Infinity);
const buttonWidths = []; // Priority+ Navigation: cached on full render, used for all resize calculations
const actionBarWrapperRef = ref(null); // position:absolute left/right — tracks viewport, unaffected by button re-renders
const moreButtonRef = ref(null);
let resizeObserver = null;

const hasSelection = computed(() => ns.selectedRecords.length > 0);
const selectionLabel = computed(() => `${ns.selectedRecords.length} selected`);
const visibleItems = computed(() => ns.actionBarItems.slice(0, visibleCount.value));
const hasOverflow = computed(() => ns.actionBarItems.length > visibleCount.value);
function isDisabled(item) {
    const { needSelected, needOneSelected, needSelectedPsp } = item.additionalData ?? {};
    const count = ns.selectedRecordsFullData.length;
    if (needSelected && !count) return true;
    if (needOneSelected && count !== 1) return true;
    if ((needSelected || needOneSelected) && needSelectedPsp) {
        return !ns.selectedByPsp[needSelectedPsp]?.length;
    }
    return false;
}

const overflowItems = computed(() => ns.actionBarItems.slice(visibleCount.value).map((item) => ({
    text: item.label,
    additionalData: { name: item.name },
    disabled: isDisabled(item),
})));

function handleAction(item) {
    if (!ns.hasExecuteAction) return;
    ns.executeAction(item.name, { additionalData: item.additionalData });
}

function handleOverflowSelect(args) {
    const name = args.item?.additionalData?.name;
    const item = ns.actionBarItems.find((i) => i.name === name);
    if (item && ns.hasExecuteAction && !isDisabled(item)) {
        ns.executeAction(item.name, { additionalData: item.additionalData });
    }
}

function cacheWidths() {
    if (!actionBarWrapperRef.value) return;
    const buttons = actionBarWrapperRef.value.querySelectorAll('.action-bar-btn');
    buttonWidths.length = 0;
    for (const btn of buttons) {
        buttonWidths.push(btn.offsetWidth);
    }
}

// priority+ approach to displaying buttons against available space
function measure() {
    if (!actionBarWrapperRef.value || !buttonWidths.length) return;

    // Wrapper is position:absolute left/right=space-6 — its clientWidth is always viewport-relative.
    // Subtract pill geometry to get the space available for action buttons:
    //   pill padding: var(--space-6)=16px each side = 32px total — update if padding token changes
    //   pill gap between deselect btn and actions: var(--space-5)=12px — update if gap token changes
    const wrapperWidth = actionBarWrapperRef.value.clientWidth;
    const deselectBtn = actionBarWrapperRef.value.querySelector('.action-bar-deselect-btn');
    const available = wrapperWidth - 32 - (deselectBtn?.offsetWidth ?? 0) - 12;

    // gap matches gap: var(--space-3) = 6px on .action-bar-actions — update both if token changes
    const gap = 6;

    // Check if all items fit with no More button needed
    const totalWidth = buttonWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);
    if (totalWidth <= available) {
        visibleCount.value = buttonWidths.length;
        return;
    }

    // Items overflow — reserve More button width (actual if rendered, fallback if not)
    const moreBtnWidth = moreButtonRef.value?.$el?.offsetWidth ?? 64;
    let accumulated = moreBtnWidth;
    let count = 0;

    for (const width of buttonWidths) {
        accumulated += gap + width;
        if (accumulated > available) break;
        count++;
    }

    visibleCount.value = count || 1;
}

onMounted(() => {
    resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => measure()));
    // If bar is already visible at mount (hasSelection already true), kick off measurement now.
    // Otherwise the hasSelection watcher below handles first-appearance measurement.
    requestAnimationFrame(() => {
        if (!actionBarWrapperRef.value) return;
        cacheWidths();
        measure();
        resizeObserver.observe(actionBarWrapperRef.value);
        nextTick(() => {
            requestAnimationFrame(() => {
                measure(); // second pass — corrects More button width once Vue renders it
                measured.value = true;
            });
        });
    });
});

onUnmounted(() => {
    resizeObserver?.disconnect();
});

// When items change (grid navigation), re-cache widths and re-measure.
// Disconnects ResizeObserver during the cycle to prevent stale-width fires while rebuilding.
watch(() => ns.actionBarItems, () => {
    if (!actionBarWrapperRef.value) return; // bar not visible — hasSelection watcher measures on first appear
    measured.value = false;
    visibleCount.value = Infinity; // show all items so cacheWidths reads full-set
    resizeObserver?.disconnect();
    nextTick(() => {
        requestAnimationFrame(() => {
            cacheWidths();
            measure();
            nextTick(() => {
                requestAnimationFrame(() => {
                    measure();
                    measured.value = true;
                    if (actionBarWrapperRef.value) resizeObserver?.observe(actionBarWrapperRef.value);
                });
            });
        });
    });
});

// Fires when bar first appears (hasSelection: false → true). Handles the case where onMounted
// couldn't measure because hasSelection was false and the wrapper wasn't in the DOM yet.
watch(hasSelection, (val) => {
    if (!val) return;
    measured.value = false;
    nextTick(() => {
        requestAnimationFrame(() => {
            cacheWidths();
            measure();
            if (actionBarWrapperRef.value) resizeObserver?.observe(actionBarWrapperRef.value);
            nextTick(() => {
                requestAnimationFrame(() => {
                    measure();
                    measured.value = true;
                });
            });
        });
    });
});
</script>

<style scoped>
.action-bar-wrapper {
    position: absolute;
    bottom: var(--space-6);
    left: var(--space-6);
    right: var(--space-6);
    display: flex;
    justify-content: center;
    align-items: flex-end;
    pointer-events: none;
    z-index: var(--z-fixed);
    opacity: 0;
    transition: opacity 0.15s ease;
}

.action-bar-wrapper.is-measured {
    opacity: 1;
}

.action-bar {
    pointer-events: all;
    background: var(--surface-dark-primary);
    border: 1px solid var(--surface-dark-primary);
    border-radius: var(--radius-md);
    padding: var(--space-4) var(--space-6);
    display: flex;
    align-items: center;
    gap: var(--space-5);
    box-shadow: var(--shadow-3);
    white-space: nowrap;
    max-width: 100%;
}

.action-bar-deselect-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    background: none;
    border: none;
    cursor: pointer;
    font-size: var(--text-size-base);
    color: rgba(255, 255, 255, 0.75);
    font-weight: var(--text-weight-medium);
    padding-right: var(--space-5);
    border-right: 1px solid rgba(255, 255, 255, 0.25);
    transition: color var(--transition-default) ease;
}

.action-bar-deselect-btn:hover {
    color: var(--text-color-inverse);
}

.action-bar-deselect-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
}

.action-bar-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
}

.action-bar-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: none;
    border: none;
    cursor: pointer;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    font-size: var(--text-size-base);
    color: rgba(255, 255, 255, 0.9);
    transition: background var(--transition-default) ease;
}

.action-bar-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
}

.action-bar-btn:disabled {
    color: rgba(255, 255, 255, 0.35);
    cursor: not-allowed;
}

.action-bar-btn:focus-visible {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: 2px;
}

.action-bar-btn[data-tooltip]::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + var(--space-5));
    left: 50%;
    transform: translateX(-50%);
    background: var(--grayscale-900);
    color: var(--text-color-inverse);
    font-size: var(--text-size-caption);
    font-weight: var(--text-weight-medium);
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-sm);
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 1;
}

.action-bar-btn[data-tooltip]:not(:disabled):hover::before {
    opacity: 1;
}

.action-bar-more-content {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
}

:deep(.e-dropdown-btn.action-bar-more-btn .e-caret) {
    display: none;
}

:deep(.e-dropdown-btn.action-bar-more-btn) {
    background: none;
    border: none;
    box-shadow: none;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    font-size: var(--text-size-base);
    color: rgba(255, 255, 255, 0.9);
    font-family: inherit;
    transition: background var(--transition-default) ease;
}

:deep(.e-dropdown-btn.action-bar-more-btn:hover) {
    background: rgba(255, 255, 255, 0.12);
    box-shadow: none;
}

:deep(.e-dropdown-btn.action-bar-more-btn:focus-visible) {
    outline: 2px solid var(--border-color-default-primary);
    outline-offset: 2px;
}

.action-bar-fade-enter-active,
.action-bar-fade-leave-active {
    transition: opacity var(--transition-default) ease, transform var(--transition-default) ease;
}

.action-bar-fade-enter-from,
.action-bar-fade-leave-to {
    opacity: 0;
    transform: translateY(var(--space-2));
}

.action-bar-fade-enter-to,
.action-bar-fade-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
